/**
 * Retranslates DOM fragments that are populated after Weglot's initial pass.
 *
 * Weglot translates the initial document, while Dawn replaces the cart markup
 * in place. Its addNodes helper applies the same DOM parsing to only that
 * fresh fragment; it never reloads the page, so the drawer's focus and
 * checkout flow remain intact.
 */
(() => {
  const CART_CONTENT_RENDERED_EVENT = 'yagcho:cart-content-rendered';
  const DYNAMIC_CONTENT_RENDERED_EVENT = 'yagcho:translate-content';
  const SALE_BADGE_SELECTOR = '[data-yagcho-weglot-sale-copy]';
  // GSC renders its global countdown bar asynchronously as a direct child of
  // <body>. Limit the observer to its stable app-specific class so no other
  // app content is sent through Weglot as a side effect.
  const GSC_COUNTDOWN_SELECTOR = '.gta-widget.gta-bar[class*="GSC-BAR-"]';
  // The app's Preact renderer creates these static labels after inserting the
  // outer bar. Timer values deliberately are not part of this selector: they
  // update every second and are not translatable copy.
  const GSC_STATIC_COPY_SELECTOR = [
    '.gta-content__text',
    '.gta-content__button',
    '.gta-content__coupon',
    '.gta-timer__unit-label',
  ].join(', ');
  const MAX_WORDS_PER_REQUEST = 75;
  // Weglot itself is loaded asynchronously by its app embed. Keep retrying for
  // a short, bounded period so a fast third-party app render cannot win the
  // race against Weglot's initialization.
  const MAX_INITIALIZATION_RETRIES = 40;
  const INITIALIZATION_RETRY_DELAY = 200;
  const MAX_GSC_RENDER_RETRIES = 20;
  const GSC_RENDER_RETRY_DELAY = 100;
  const TRANSLATABLE_ATTRIBUTES = ['alt', 'aria-label', 'placeholder', 'title'];
  const EXCLUDED_TAG_NAMES = new Set(['NOSCRIPT', 'SCRIPT', 'STYLE', 'SVG', 'TEMPLATE']);
  const activeTranslations = new WeakMap();
  const gscSourceText = new WeakMap();
  // Weglot's public language-change event is not emitted consistently by every
  // integration mode. Keep the exact target language requested for each GSC
  // bar so its third-party DOM can be translated from the HTML language signal.
  const gscTargetLanguage = new WeakMap();
  const observedGscBars = new WeakSet();
  let drawerContentObserver;
  let observedDrawerContent;
  let gscCountdownObserver;
  let gscLanguageObserver;
  let observedDocumentLanguage;
  let observedWeglot;

  document.addEventListener(CART_CONTENT_RENDERED_EVENT, (event) => {
    observeCartDrawerContent();
    scheduleContainers(event.detail?.containers || [event.target]);
  });

  // Product and plan choices in the subscription drawer replace editor text
  // after the initial document translation.  Re-run Weglot only for that
  // component, rather than reloading the page or reopening the drawer.
  document.addEventListener(DYNAMIC_CONTENT_RENDERED_EVENT, (event) => {
    scheduleContainers(event.detail?.containers || [event.target]);
  });

  // The campaign badges are stored as editor content. Run Weglot's dynamic
  // scanner for those small, isolated nodes after its initial document pass so
  // every non-default Sale context receives the current language consistently.
  document.addEventListener('shopify:section:load', (event) => {
    if (!(event.target instanceof Element)) return;
    scheduleContainers(event.target.querySelectorAll(SALE_BADGE_SELECTOR));
  });

  function scheduleContainers(containers) {
    const seenContainers = new Set();
    const targets = containers instanceof Element ? [containers] : containers;

    [...targets].forEach((container) => {
      if (!(container instanceof Element) || seenContainers.has(container)) return;

      seenContainers.add(container);
      scheduleTranslation(container);
    });
  }

  function observeCartDrawerContent() {
    const drawerContent = document.querySelector('cart-drawer #CartDrawer');
    if (!drawerContent || drawerContent === observedDrawerContent) return;

    drawerContentObserver?.disconnect();
    observedDrawerContent = drawerContent;
    drawerContentObserver = new MutationObserver((mutations) => {
      const drawerWasReplaced = mutations.some(
        (mutation) =>
          mutation.type === 'childList' &&
          mutation.target === drawerContent &&
          (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0),
      );

      if (drawerWasReplaced) scheduleTranslation(drawerContent);
    });
    drawerContentObserver.observe(drawerContent, { childList: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeDynamicContentTranslation, { once: true });
  } else {
    initializeDynamicContentTranslation();
  }

  function initializeDynamicContentTranslation() {
    observeCartDrawerContent();
    observeGscCountdown();
    observeGscLanguage();
    // This script is deferred while the subscription drawer initializes inline.
    // Translate its first dynamic render even though its event already fired.
    scheduleContainers(document.querySelectorAll('[data-legacy-drawer-root]'));
    // Sale badges are merchant-managed settings and may be missed by Weglot's
    // initial scan when a market context provides them. This applies to every
    // Sale page and current Weglot target language, not to one named market.
    scheduleContainers(document.querySelectorAll(SALE_BADGE_SELECTOR));
    // The external GSC app may have rendered before this deferred script, or
    // it may append the bar later. Register already-present instances here;
    // observeGscCountdown registers every later mount without observing timer
    // digits as they update.
    registerGscCountdownBars(document.querySelectorAll(GSC_COUNTDOWN_SELECTOR));
  }

  function observeGscCountdown() {
    if (!document.body || gscCountdownObserver) return;

    gscCountdownObserver = new MutationObserver((mutations) => {
      const countdownBars = [];

      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;

          if (node.matches(GSC_COUNTDOWN_SELECTOR)) countdownBars.push(node);
          countdownBars.push(...node.querySelectorAll(GSC_COUNTDOWN_SELECTOR));
        });
      });

      if (countdownBars.length) registerGscCountdownBars(countdownBars);
    });

    // GSC prepends its bar to <body>; observing only direct children avoids
    // reacting to the timer's regular internal updates every second.
    gscCountdownObserver.observe(document.body, { childList: true });
  }

  function observeGscLanguage() {
    if (!document.documentElement || gscLanguageObserver) return;

    observedDocumentLanguage = document.documentElement.lang;
    gscLanguageObserver = new MutationObserver((mutations) => {
      const languageChanged = mutations.some(
        (mutation) => mutation.type === 'attributes' && mutation.attributeName === 'lang',
      );
      const languageTo = document.documentElement.lang;

      if (!languageChanged || !languageTo || languageTo === observedDocumentLanguage) return;

      observedDocumentLanguage = languageTo;
      scheduleGscCountdownTranslation(languageTo);
    });

    // Weglot updates <html lang> for every active storefront language. Unlike
    // its optional JavaScript event, this mutation also occurs for the current
    // Shopify app integration and therefore reliably tracks manual switches.
    gscLanguageObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['lang'],
    });
  }

  function registerGscCountdownBars(containers) {
    const bars = containers instanceof Element ? [containers] : containers;

    [...bars].forEach((bar) => {
      if (!(bar instanceof Element) || !bar.matches(GSC_COUNTDOWN_SELECTOR)) return;

      observeGscCountdownBar(bar);
      scheduleTranslation(bar);
    });
  }

  function scheduleGscCountdownTranslation(languageTo) {
    if (typeof languageTo !== 'string' || !languageTo) return;

    document.querySelectorAll(GSC_COUNTDOWN_SELECTOR).forEach((bar) => {
      observeGscCountdownBar(bar);
      // Use the language from the mutation itself. Weglot can fire its DOM
      // updates before getCurrentLang() reflects the new active language.
      gscTargetLanguage.set(bar, languageTo);
      scheduleTranslation(bar);
    });
  }

  function observeGscCountdownBar(bar) {
    if (observedGscBars.has(bar)) return;

    observedGscBars.add(bar);
    const barObserver = new MutationObserver((mutations) => {
      const staticCopyWasRendered = mutations.some((mutation) =>
        [...mutation.addedNodes].some(
          (node) =>
            node instanceof Element &&
            (node.matches(GSC_STATIC_COPY_SELECTOR) || node.querySelector(GSC_STATIC_COPY_SELECTOR)),
        ),
      );

      if (staticCopyWasRendered) scheduleTranslation(bar);
    });

    // Preact fills the already-mounted bar asynchronously. Watch only for the
    // specific static-copy elements, not text-node updates inside the timer.
    barObserver.observe(bar, { childList: true, subtree: true });
  }

  function scheduleTranslation(container) {
    const token = Symbol('cart-content-translation');
    activeTranslations.set(container, token);

    window.requestAnimationFrame(() => translateContainer(container, token));
  }

  function translateContainer(container, token, retryCount = 0) {
    if (!container.isConnected || activeTranslations.get(container) !== token) return;

    const weglot = getWeglot();
    if (!weglot || weglot.initialized === false) {
      if (retryCount < MAX_INITIALIZATION_RETRIES) {
        window.setTimeout(
          () => translateContainer(container, token, retryCount + 1),
          INITIALIZATION_RETRY_DELAY,
        );
      }
      return;
    }

    let languageTo = container.matches(GSC_COUNTDOWN_SELECTOR) ? gscTargetLanguage.get(container) : null;
    if (!languageTo) {
      try {
        languageTo = weglot.getCurrentLang();
      } catch (_) {
        return;
      }
    }

    if (!languageTo) return;

    observeWeglotLanguageChanges(weglot);

    // GSC mounts a third-party Preact tree after Weglot's initial document
    // pass. Translate its isolated static copy directly through Weglot's
    // public API instead of relying on a generic DOM re-scan that the app can
    // replace during its own rendering cycle.
    if (container.matches(GSC_COUNTDOWN_SELECTOR)) {
      translateGscCountdown(container, token, weglot, languageTo);
      return;
    }

    // Use Weglot's own dynamic-DOM parser when it is available. It preserves
    // Weglot's node merging and attribute handling, which a plain text-node
    // replacement cannot reliably reproduce.
    if (typeof weglot.addNodes === 'function') {
      try {
        Promise.resolve(weglot.addNodes(container)).catch(() => {
          translateWithPublicApi(container, token, weglot, languageTo);
        });
        return;
      } catch (_) {
        // Older embeds without a working dynamic-DOM helper use the documented
        // translation API below as a safe fallback.
      }
    }

    translateWithPublicApi(container, token, weglot, languageTo);
  }

  function observeWeglotLanguageChanges(weglot) {
    if (observedWeglot === weglot || typeof weglot.on !== 'function') return;

    observedWeglot = weglot;
    weglot.on('languageChanged', (languageTo) => {
      // Some Weglot modes dispatch this event, others only update <html lang>.
      // Supporting both signals keeps the adapter independent of that mode.
      scheduleGscCountdownTranslation(
        typeof languageTo === 'string' && languageTo ? languageTo : document.documentElement.lang,
      );
    });
  }

  function translateGscCountdown(container, token, weglot, languageTo, retryCount = 0) {
    const entries = collectGscTranslatableEntries(container);

    // The GSC root is inserted before Preact mounts its visible content. Wait
    // for that static copy rather than treating an empty first scan as final.
    if (!entries.length) {
      if (retryCount < MAX_GSC_RENDER_RETRIES) {
        window.setTimeout(
          () => translateGscCountdown(container, token, weglot, languageTo, retryCount + 1),
          GSC_RENDER_RETRY_DELAY,
        );
      }
      return;
    }

    translateWithPublicApi(container, token, weglot, languageTo, entries);
  }

  function translateWithPublicApi(container, token, weglot, languageTo, entries = collectTranslatableEntries(container)) {
    for (let start = 0; start < entries.length; start += MAX_WORDS_PER_REQUEST) {
      const batch = entries.slice(start, start + MAX_WORDS_PER_REQUEST);
      requestTranslations(weglot, batch, languageTo)
        .then((translations) => applyTranslations(container, token, batch, translations))
        .catch(() => {
          // Weglot remains authoritative for this optional client-side layer.
          // Keep the source DOM usable if its request is unavailable.
        });
    }
  }

  function collectGscTranslatableEntries(container) {
    const entries = [];
    const staticCopyElements = container.querySelectorAll(GSC_STATIC_COPY_SELECTOR);

    staticCopyElements.forEach((element) => {
      if (isExcludedElement(element)) return;

      const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) =>
          isTranslatableTextNode(node) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT,
      });

      let textNode;
      while ((textNode = walker.nextNode())) {
        const currentValue = textNode.nodeValue;
        const sourceValue = gscSourceText.get(textNode) || currentValue;
        const sourceMatch = sourceValue.match(/^(\s*)(.*?)(\s*)$/s);
        const currentMatch = currentValue.match(/^(\s*)(.*?)(\s*)$/s);
        const sourceText = sourceMatch?.[2];

        if (!sourceText || !currentMatch) continue;

        entries.push({
          sourceText,
          apply: (translation) => {
            if (!textNode.isConnected || textNode.nodeValue !== currentValue) return;

            gscSourceText.set(textNode, sourceValue);
            textNode.nodeValue = `${currentMatch[1]}${translation}${currentMatch[3]}`;
          },
        });
      }
    });

    return entries;
  }

  function getWeglot() {
    const weglot = window.Weglot;
    if (!weglot || typeof weglot.translate !== 'function' || typeof weglot.getCurrentLang !== 'function') return null;

    return weglot;
  }

  function collectTranslatableEntries(container) {
    const entries = [];
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => (isTranslatableTextNode(node) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT),
    });

    let textNode;
    while ((textNode = walker.nextNode())) {
      const originalValue = textNode.nodeValue;
      const match = originalValue.match(/^(\s*)(.*?)(\s*)$/s);
      const sourceText = match?.[2];
      if (!sourceText) continue;

      entries.push({
        sourceText,
        apply: (translation) => {
          if (!textNode.isConnected || textNode.nodeValue !== originalValue) return;
          textNode.nodeValue = `${match[1]}${translation}${match[3]}`;
        },
      });
    }

    const elements = [container, ...container.querySelectorAll('*')];
    elements.forEach((element) => {
      if (isExcludedElement(element)) return;

      TRANSLATABLE_ATTRIBUTES.forEach((attribute) => {
        const originalValue = element.getAttribute(attribute);
        if (!isTranslatableValue(originalValue)) return;

        entries.push({
          sourceText: originalValue,
          apply: (translation) => {
            if (!element.isConnected || element.getAttribute(attribute) !== originalValue) return;
            element.setAttribute(attribute, translation);
          },
        });
      });
    });

    return entries;
  }

  function isTranslatableTextNode(node) {
    const parent = node.parentElement;
    return Boolean(parent && !isExcludedElement(parent) && isTranslatableValue(node.nodeValue));
  }

  function isExcludedElement(element) {
    return (
      EXCLUDED_TAG_NAMES.has(element.tagName) ||
      Boolean(element.closest('[data-wg-notranslate], .notranslate'))
    );
  }

  function isTranslatableValue(value) {
    return Boolean(value && /\p{L}/u.test(value));
  }

  function requestTranslations(weglot, entries, languageTo) {
    const words = entries.map(({ sourceText }) => ({ t: 1, w: sourceText }));

    try {
      return Promise.resolve(
        weglot.translate({
          // `words` is Weglot's documented current payload. `phrases` keeps the
          // existing Shopify app embed compatible with older Weglot runtimes.
          words,
          phrases: words,
          languageTo,
        }),
      );
    } catch (error) {
      return Promise.reject(error);
    }
  }

  function applyTranslations(container, token, entries, translations) {
    if (activeTranslations.get(container) !== token || !Array.isArray(translations)) return;

    translations.forEach((translation, index) => {
      if (typeof translation === 'string' && translation) entries[index]?.apply(translation);
    });
  }
})();
